import { z } from 'zod'
import {
  applyMaintenanceActionResponseSchema,
  databasesResponseSchema,
  dumpsResponseSchema,
  dumpUrlResponseSchema,
  engineLogsResponseSchema,
  engineResponseSchema,
  engineUptimeResponseSchema,
  environmentsResponseSchema,
  meResponseSchema,
  sessionsResponseSchema,
  signOutResponseSchema,
  triggerDumpResponseSchema,
  type ApplyMaintenanceActionRequest,
  type ApplyMaintenanceActionResponse,
  type DatabasesResponse,
  type DumpsResponse,
  type DumpUrlResponse,
  type EngineLogsResponse,
  type EngineResponse,
  type EngineUptimeResponse,
  type EnvironmentsResponse,
  type MeResponse,
  type SessionsResponse,
  type SignOutResponse,
  type TriggerDumpResponse,
} from '@cloudcat-apps/admin-shared'
import { getAccessToken } from './auth.js'
import { loadConfig } from './config.js'

class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

async function authedFetch(path: string, init?: RequestInit): Promise<Response> {
  const cfg = await loadConfig()
  const token = await getAccessToken()
  if (!token) throw new ApiError(401, 'Not authenticated')
  const res = await fetch(`${cfg.apiUrl}${path}`, {
    ...init,
    headers: {
      ...(init?.headers ?? {}),
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  })
  if (!res.ok) {
    const text = await res.text().catch(() => '')
    throw new ApiError(res.status, `${res.status} ${res.statusText}: ${text}`)
  }
  return res
}

async function call<T extends z.ZodTypeAny>(
  schema: T,
  path: string,
  init?: RequestInit,
): Promise<z.infer<T>> {
  const res = await authedFetch(path, init)
  return schema.parse(await res.json())
}

export const api = {
  me: (): Promise<MeResponse> => call(meResponseSchema, '/api/me'),
  databases: (): Promise<DatabasesResponse> => call(databasesResponseSchema, '/api/databases'),
  environments: (): Promise<EnvironmentsResponse> =>
    call(environmentsResponseSchema, '/api/environments'),
  engine: (): Promise<EngineResponse> => call(engineResponseSchema, '/api/engine'),
  engineUptime: (): Promise<EngineUptimeResponse> =>
    call(engineUptimeResponseSchema, '/api/engine/uptime'),
  engineLogs: (): Promise<EngineLogsResponse> => call(engineLogsResponseSchema, '/api/engine/logs'),
  /**
   * One Postgres log file as text. A Blob rather than a link because the
   * route needs the bearer token, which a plain navigation would not send.
   */
  engineLogFile: async (fileName: string): Promise<Blob> => {
    const res = await authedFetch(`/api/engine/logs/download?file=${encodeURIComponent(fileName)}`)
    return res.blob()
  },
  applyMaintenanceAction: (
    request: ApplyMaintenanceActionRequest,
  ): Promise<ApplyMaintenanceActionResponse> =>
    call(applyMaintenanceActionResponseSchema, '/api/engine/maintenance-actions', {
      method: 'POST',
      body: JSON.stringify(request),
    }),
  dumps: (database: string): Promise<DumpsResponse> =>
    call(dumpsResponseSchema, `/api/dumps?database=${encodeURIComponent(database)}`),
  dumpUrl: (key: string): Promise<DumpUrlResponse> =>
    call(dumpUrlResponseSchema, `/api/dumps/url?key=${encodeURIComponent(key)}`),
  triggerDump: (databaseName: string): Promise<TriggerDumpResponse> =>
    call(triggerDumpResponseSchema, `/api/databases/${encodeURIComponent(databaseName)}/dump`, {
      method: 'POST',
    }),
  sessions: (): Promise<SessionsResponse> => call(sessionsResponseSchema, '/api/sessions'),
  signOutUser: (username: string): Promise<SignOutResponse> =>
    call(signOutResponseSchema, `/api/sessions/${encodeURIComponent(username)}/sign-out`, {
      method: 'POST',
    }),
}

export { ApiError }
